import {
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Description,
  MarkEmailReadOutlined,
  MarkEmailUnreadOutlined,
  NotificationsActiveOutlined,
  NotificationsNoneOutlined,
} from "@mui/icons-material";
import { useTaskWatchingContext } from "@/contexts/TaskWatchingContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { useFilteredNotifications } from "@/hooks/useFilteredNotifications";
import { PatchTask } from "@/services/fhir/task/PatchTask";
import {
  getPatientIdForSummaryFromTask,
  isQuestionnaireAnsweredNotificationTask,
} from "@/utils/taskNotificationUtils";
import { currentFeatures } from "@/configs/Settings";
import dayjs from "dayjs";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

/** Formata data em pt-BR (dd/mm/aaaa, hh:mm) — dayjs alinhado ao restante do projeto */
function formatDateTimePtBr(dateIsoString?: string): string {
  if (!dateIsoString) return "";
  const parsed = dayjs(dateIsoString);
  if (!parsed.isValid()) return "";
  return parsed.format("DD/MM/YYYY HH:mm");
}

/**
 * Bloco de notificações em destaque no dashboard.
 * Reutiliza TaskWatchingContext (mesma fonte do HeaderNotification).
 * Visual evoluído: cards, badge de não lidas, hierarquia clara.
 */
export function DashboardNotifications() {
  const { user, access } = useSessionContext();
  const { allTasks, updateTaskInContext } = useTaskWatchingContext();
  const navigate = useNavigate();
  const { filteredTasks, unreadCount } = useFilteredNotifications({
    allTasks,
    user,
    access,
  });

  const [updatingById, setUpdatingById] = useState<Record<string, boolean>>({});

  const handleToggleReadStatus = async (task: any) => {
    try {
      setUpdatingById((prev) => ({ ...prev, [task.id]: true }));
      const isCompleted = task?.status === "completed";
      const nextStatus = isCompleted ? "requested" : "completed";

      await PatchTask(task.id, [
        { op: "replace", path: "/status", value: nextStatus },
      ]);
      updateTaskInContext(task.id, { status: nextStatus });
    } catch (error) {
      console.error("Erro ao marcar tarefa como lida:", error);
    } finally {
      setUpdatingById((prev) => ({ ...prev, [task.id]: false }));
    }
  };

  const handleAccessMedicalRecord = async (task: any) => {
    const taskId = task?.id;
    if (!taskId) return;
    try {
      setUpdatingById((prev) => ({ ...prev, [taskId]: true }));

      const patientId = getPatientIdForSummaryFromTask(task, {
        isDefaultApp: Boolean(currentFeatures.defaultApp),
      });

      console.log("patientId", patientId);
      if (!patientId) {
        console.warn(
          "Não foi possível derivar patientId para /summary a partir da task",
          task,
        );
        return;
      }

      if (task?.status !== "completed") {
        await PatchTask(taskId, [
          { op: "replace", path: "/status", value: "completed" },
        ]);
        updateTaskInContext(taskId, { status: "completed" });
      }

      navigate("/summary", { state: { patientId } });
    } catch (error) {
      console.error("Erro ao acessar prontuário via notificação:", error);
    } finally {
      setUpdatingById((prev) => ({ ...prev, [taskId]: false }));
    }
  };

  // Bloco sempre visível: com ou sem notificações (estado vazio exibido quando não houver)
  const hasTasks = filteredTasks.length > 0;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid",
        borderColor: unreadCount > 0 ? "warning.main" : "neutral200.main",
      }}
    >
      {/* Cabeçalho em destaque */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 2,
          py: 1.5,

          borderBottom: "1px solid",
          borderColor: "neutral200.main",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {unreadCount > 0 ? (
            <NotificationsActiveOutlined
              sx={{ fontSize: 28, color: "warning.main" }}
            />
          ) : (
            <NotificationsNoneOutlined
              sx={{ fontSize: 28, color: "text.secondary" }}
            />
          )}
          <Typography variant="subtitle1" fontWeight={600}>
            Notificações
          </Typography>
          {unreadCount > 0 && (
            <Chip
              label={
                unreadCount === 1 ? "1 não lida" : `${unreadCount} não lidas`
              }
              color="warning"
              size="small"
              sx={{ fontWeight: 600 }}
            />
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          últimos 30 dias
        </Typography>
      </Stack>

      {/* Lista de notificações com scroll independente (altura máxima para manter cabeçalho visível) */}
      <Stack
        spacing={1.5}
        sx={{
          p: 2,
          maxHeight: 320,
          overflowY: "auto",
          // Estilização da scrollbar personalizada
          "&::-webkit-scrollbar": { width: "0.5em" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "primary700.main",
            borderRadius: 1,
          },
        }}
      >
        {!hasTasks ? (
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            sx={{ py: 2 }}
          >
            Nenhuma notificação nos últimos 30 dias.
          </Typography>
        ) : (
          filteredTasks.map((task) => (
            <Card
              key={task.id}
              variant="outlined"
              sx={{
                borderRadius: 1.5,
                borderColor:
                  task.status === "requested"
                    ? "warning.main"
                    : "neutral200.main",
                bgcolor:
                  task.status === "requested"
                    ? "warning100.main"
                    : "neutral0.main",
                transition: "all 0.2s ease",
                // Impede que o card seja comprimido quando há muitas notificações
                flexShrink: 0,
                minHeight: "fit-content",
                "&:hover": {
                  borderColor: "primary.main",
                  boxShadow: 1,
                },
              }}
            >
              <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Stack flex={1} spacing={0.5} minWidth={0}>
                    <Typography
                      variant="body1"
                      fontWeight={task.status === "requested" ? 600 : 500}
                      sx={{ wordBreak: "break-word" }}
                    >
                      {task.description || "Tarefa sem descrição"}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {typeof task.code === "object" && task.code.text
                        ? task.code.text
                        : "Sem detalhes adicionais"}
                    </Typography>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ mt: 1 }}
                    >
                      <Chip
                        label={
                          task.status === "requested"
                            ? "Não lido"
                            : task.status === "completed"
                              ? "Lido"
                              : task.status || "pendente"
                        }
                        color={
                          task.status === "completed"
                            ? "success"
                            : task.status === "in-progress"
                              ? "primary"
                              : "warning"
                        }
                        size="small"
                      />
                      {task?.authoredOn && (
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTimePtBr(task.authoredOn)}
                        </Typography>
                      )}
                    </Stack>
                    {isQuestionnaireAnsweredNotificationTask(task) ? (
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Description />}
                          onClick={() => void handleAccessMedicalRecord(task)}
                          disabled={updatingById[task.id]}
                        >
                          Acessar prontuário
                        </Button>
                      </Stack>
                    ) : null}
                  </Stack>
                  {updatingById[task.id] ? (
                    <Skeleton variant="circular" width={36} height={36} />
                  ) : (
                    <Tooltip
                      title={
                        task.status === "completed"
                          ? "Marcar como não lido"
                          : "Marcar como lido"
                      }
                      arrow
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleToggleReadStatus(task)}
                        sx={{ flexShrink: 0 }}
                      >
                        {task.status === "requested" ? (
                          <MarkEmailUnreadOutlined color="warning" />
                        ) : (
                          <MarkEmailReadOutlined color="success" />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ))
        )}
      </Stack>
    </Paper>
  );
}
